// 010230.com transmission log: line generators, the in-world clock and the audio (static hiss + chirps).
import { mediaUrl } from "@/lib/api";

export const GLYPHS = "▓▒░#%&@$*/\\|<>{}[]=+~^?!0123456789ABCDEFX";
export const KEY = "FREQ 333 // RADIO // LISTEN";
const WORDS = ["SIG", "TX", "RELAY", "NODE", "ERR", "PKT", "LOSS", "SYNC", "CARRIER", "HELPERS", "LIST", "SUBJ", "TRACE", "333"];

export const rnd = (a, b) => a + Math.random() * (b - a);
export const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
export const glyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];

export const narrow = () => window.innerWidth < 640;

// Body characters that fit on one line next to the timestamp (no wrapping on phones). Block glyphs are wider than
// letters, so the per-character width is deliberately generous.
export function maxChars() {
  const w = Math.min(window.innerWidth, 900);
  return narrow() ? Math.max(16, Math.floor((w - 32) / 7.6) - 11) : Math.max(24, Math.floor((w - 80) / 8.8) - 23);
}

export function noise(cap) {
  const len = Math.floor(rnd(0.45, 1) * cap);
  let s = "";
  while (s.length < len) s += Math.random() < 0.16 ? ` ${pick(WORDS)} ` : glyph();
  return s.slice(0, len);
}

export const fit = (text, cap) => (text.length > cap ? text.slice(0, cap - 1) + glyph() : text);

// Timestamps creep toward 01/02/2030 03:33:00 but never reach it; near it the signal resyncs further back.
export class Clock {
  constructor() { this.target = 3 * 3600 + 33 * 60; this.cur = this.target - Math.floor(rnd(780, 1320)); }
  next() {
    const left = this.target - this.cur;
    if (left <= 2) this.cur = this.target - Math.floor(rnd(600, 1100));
    else this.cur += Math.max(1, Math.min(left - 1, Math.round(left * rnd(0.004, 0.02)) || 1));
    return this.fmt();
  }
  fmt() {
    const h = Math.floor(this.cur / 3600), m = Math.floor((this.cur % 3600) / 60), s = this.cur % 60;
    const t = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return narrow() ? `[${t}]` : `[01/02/2030 ${t}]`;
  }
}

export class LogAudio {
  async start(hasStatic) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    await this.ctx.resume();
    this.master = this.ctx.createGain();
    this.master.gain.value = 1;
    this.master.connect(this.ctx.destination);
    const hiss = this.ctx.createGain();
    hiss.gain.value = 0.07;  // faint
    hiss.connect(this.master);
    let buf = null;
    if (hasStatic) {
      try { buf = await this.ctx.decodeAudioData(await (await fetch(mediaUrl("radio_static"))).arrayBuffer()); } catch (e) { buf = null; }
    }
    const src = this.ctx.createBufferSource();
    if (buf) { src.buffer = buf; src.connect(hiss); } else {
      const n = this.ctx.sampleRate * 2, b = this.ctx.createBuffer(1, n, this.ctx.sampleRate), d = b.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
      const bp = this.ctx.createBiquadFilter();
      bp.type = "bandpass"; bp.frequency.value = 1800; bp.Q.value = 0.6;
      src.buffer = b; src.connect(bp); bp.connect(hiss);
    }
    src.loop = true;
    src.start();
  }
  chirp() {  // small digital chirps when the key line decodes
    if (!this.ctx) return;
    const t0 = this.ctx.currentTime;
    for (let i = 0; i < 4; i++) {
      const o = this.ctx.createOscillator(), g = this.ctx.createGain(), t = t0 + i * 0.07;
      o.type = "square"; o.frequency.setValueAtTime(rnd(1200, 3400), t);
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.045, t + 0.005); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
      o.connect(g); g.connect(this.master); o.start(t); o.stop(t + 0.06);
    }
  }
  burst() {  // static burst for the INTEGRITY 3% blackout
    if (!this.ctx) return;
    const n = Math.floor(this.ctx.sampleRate * 1.6), b = this.ctx.createBuffer(1, n, this.ctx.sampleRate), d = b.getChannelData(0);
    let hold = 0, v = 0;
    for (let i = 0; i < n; i++) { if (hold-- <= 0) { v = Math.random() * 2 - 1; hold = 2 + Math.floor(Math.random() * 18); } d[i] = v * (1 - i / n); }
    const src = this.ctx.createBufferSource(), g = this.ctx.createGain();
    g.gain.value = 0.18; src.buffer = b; src.connect(g); g.connect(this.master); src.start();
  }
  setMuted(m) { if (this.master) this.master.gain.value = m ? 0 : 1; }
  stop() { try { this.ctx && this.ctx.close(); } catch (e) { /* closed */ } }
}
